/**
 * Voorlezen: een alinea als audio, met de knop om te stoppen. En de
 * vertaling van een alinea in de moedertaal (in het vak van Anna Maria),
 * als dat voor deze leerling kan.
 *
 * Er speelt hooguit één ding tegelijk. Op dezelfde knop nog eens klikken
 * stopt het; iets anders starten stopt het vorige. Gaat het mis, dan zegt
 * Anna Maria het (via `meld`; bij de vertaling via `meldBijVertaling`, zodat
 * de vertaling zelf blijft staan). Verdwijnt de vertaling uit het vak, dan
 * stopt hij (`stopVertaling`).
 *
 * `laadt` en `speelt` zijn het id van de alinea, of bij de vertaling
 * vertalingSleutel(id): zo licht de alinea in de tekst niet op als zijn
 * vertaling klinkt.
 */
import { useEffect, useRef, useState } from 'react';
import { leerlingApi as api } from '../../../api/leerling';

export const vertalingSleutel = (alineaId) => `vertaling:${alineaId}`;

export function useVoorlezen(meld, leerlingLesId, meldBijVertaling = meld) {
  const [laadt, setLaadt] = useState(null);     // waarvoor de audio onderweg is
  const [speelt, setSpeelt] = useState(null);   // wat nu klinkt
  const audio = useRef(null);
  const gewild = useRef(null);                  // wat er moet klinken; komt de audio later binnen, dan alleen dat

  const stop = () => {
    if (audio.current) {
      audio.current.pause();
      audio.current.currentTime = 0;
    }
  };

  const speel = async (sleutel, haal, melden = meld) => {
    if (speelt === sleutel && audio.current) {
      stop();
      setSpeelt(null);
      return;
    }
    stop();
    gewild.current = sleutel;
    setLaadt(sleutel);
    try {
      const blob = await haal();
      if (gewild.current !== sleutel) return;   // intussen iets anders gekozen, of de vertaling is weg
      const url = URL.createObjectURL(blob);
      const geluid = new Audio(url);
      audio.current = geluid;
      geluid.onended = () => { setSpeelt(null); URL.revokeObjectURL(url); };
      geluid.onerror = () => {
        setSpeelt(null);
        melden('Oeps, er ging iets mis bij het afspelen van de audio.');
        URL.revokeObjectURL(url);
      };
      setSpeelt(sleutel);
      await geluid.play();
    } catch (err) {
      melden(`Oeps, voorlezen is niet beschikbaar: ${err.message}`);
    } finally {
      setLaadt(null);
    }
  };

  const leesVoor = (alineaId, tekst) => speel(alineaId, () => api.leesVoor(alineaId, tekst, leerlingLesId));
  const leesVertalingVoor = (alineaId, huidigNiveau) =>
    speel(vertalingSleutel(alineaId), () => api.leesVertalingVoor(alineaId, leerlingLesId, huidigNiveau), meldBijVertaling);
  /** Klinkt (of laadt) er een vertaling die niet `behalve` is, dan stoppen: hij staat niet meer in het vak. */
  const stopVertaling = (behalve = null) => {
    const huidig = gewild.current;
    if (typeof huidig === 'string' && huidig.startsWith('vertaling:') && huidig !== behalve) {
      gewild.current = null;
      stop();
      setSpeelt(null);
    }
  };

  // Weg van het scherm: stoppen.
  useEffect(() => () => {
    if (audio.current) {
      audio.current.pause();
      audio.current = null;
    }
  }, []);

  return { laadt, speelt, leesVoor, leesVertalingVoor, stopVertaling };
}
